import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useNavigate, useParams } from "react-router";

import * as Api from "@/api";
import { HELP_PHONEBOOKS } from "@/constants/HelpUrls";
import ContentLoader from "@/design/components/ContentLoader";
import FabBar from "@/design/components/FabBar";
import Page from "@/design/layouts/Page";
import { disableSearch, enableSearch, setHelpUrl } from "@/store/reducers/ui";
import useSearch from "@/utils/hooks/useSearch";
import { toggleLoadingState } from "@/utils/utility";
import Valkyrie, { viAddressBook, viPlus } from "@sippy-platform/valkyrie";

import { Button, Grid, Typography } from "@mui/material";
import { useHotkey } from "@tanstack/react-hotkeys";

import PhonebookCard from "./PhonebookCard";

function Phonebooks() {
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const pbx = useSelector((state) => state.domain.pbx);
  const phonebookStore = useSelector((state) => state.domain.phonebooks);
  const searchValue = useSelector((state) => state.ui.searchValue);
  const progressBar = useSelector((state) => state.ui.progressBar);

  useEffect(() => {
    dispatch(setHelpUrl(HELP_PHONEBOOKS));
  }, [dispatch]);

  useHotkey("Alt+N", () => navigate(`/pbx/${params.pbxId}/phonebooks/new`));

  // Initializing
  useEffect(() => {
    async function getData() {
      toggleLoadingState(true);
      await Promise.resolve(Api.getPhonebooks()).finally(toggleLoadingState);
    }

    getData();
  }, []);

  // Search every time holidaySetList or searchQuery changes
  const searchResults = useSearch(phonebookStore.list, searchValue, ["name"]);

  useEffect(() => {
    dispatch(enableSearch());
  }, [dispatch]);
  useEffect(() => () => dispatch(disableSearch()), [dispatch]);

  return (
    <Page maxWidth="xxl">
      <title>{`Phonebooks \u00b7 ${pbx.name} \u00b7 ${import.meta.env.SIPPY_APP_TITLE}`}</title>
      <Grid container spacing={1}>
        <Grid size={12}>
          <Typography variant="h5" component="h2" sx={{ my: 2 }}>
            Phonebooks
          </Typography>
        </Grid>
        {phonebookStore.meta.count && searchResults.length >= 1 ? (
          searchResults.map((phonebook) => (
            <Grid key={phonebook.id} size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 2 }}>
              <PhonebookCard phonebook={phonebook} />
            </Grid>
          ))
        ) : (
          <ContentLoader
            count={phonebookStore.meta.count}
            loading={progressBar}
            noItems="You don't have any phonebooks yet"
            noResults="No phonebooks in your current query"
            fetching="Getting your phonebooks"
            icon={viAddressBook}
          />
        )}
      </Grid>
      <FabBar>
        <Button variant="contained" component={NavLink} to={`new`} startIcon={<Valkyrie icon={viPlus} />}>
          Add
        </Button>
      </FabBar>
    </Page>
  );
}

export default Phonebooks;
